// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Every joint of both hands, read out of a frame (14 Sep 2026).
//
// `xrInput.ts` reads the six joints a decision needs — the tip, the thumb, the palm and three
// fingertips for the fist — and nothing else, because nothing else decided anything. Drawing a
// hand needs all twenty-five, and their radii, and it needs them in the space the machine is
// drawn in. This is that reader and only that: it fills two flat arrays a frame and decides
// nothing. `ghostHands.ts` draws what it fills.
//
// **Kept apart from `HandReport`** because that shape is shared with the native client by
// fixture and by intent — a decision's input — and a picture's input is a different thing that
// changes for different reasons (a runtime that adds a joint, a hand model that wants a bone the
// spec does not name).

import type { XRFramePort, XRInputSourcePort } from './xrInput'

/** The WebXR Hand Input joint names, in the spec's order. Index 0 is the wrist. */
export const HAND_JOINT_NAMES = [
  'wrist',
  'thumb-metacarpal',
  'thumb-phalanx-proximal',
  'thumb-phalanx-distal',
  'thumb-tip',
  'index-finger-metacarpal',
  'index-finger-phalanx-proximal',
  'index-finger-phalanx-intermediate',
  'index-finger-phalanx-distal',
  'index-finger-tip',
  'middle-finger-metacarpal',
  'middle-finger-phalanx-proximal',
  'middle-finger-phalanx-intermediate',
  'middle-finger-phalanx-distal',
  'middle-finger-tip',
  'ring-finger-metacarpal',
  'ring-finger-phalanx-proximal',
  'ring-finger-phalanx-intermediate',
  'ring-finger-phalanx-distal',
  'ring-finger-tip',
  'pinky-finger-metacarpal',
  'pinky-finger-phalanx-proximal',
  'pinky-finger-phalanx-intermediate',
  'pinky-finger-phalanx-distal',
  'pinky-finger-tip',
] as const

export const HAND_JOINT_COUNT = HAND_JOINT_NAMES.length

/**
 * The bones a hand is drawn with, as pairs of joint indices: each finger from the wrist to its
 * tip, and the three across the knuckles that close the palm.
 */
export const HAND_BONES: readonly (readonly [number, number])[] = [
  [0, 1], [1, 2], [2, 3], [3, 4],
  [0, 5], [5, 6], [6, 7], [7, 8], [8, 9],
  [0, 10], [10, 11], [11, 12], [12, 13], [13, 14],
  [0, 15], [15, 16], [16, 17], [17, 18], [18, 19],
  [0, 20], [20, 21], [21, 22], [22, 23], [23, 24],
  [6, 11], [11, 16], [16, 21],
]

/** A joint the runtime gives no radius for is drawn this big, metres. */
export const DEFAULT_JOINT_RADIUS = 0.008

/** One hand's joints for a frame: positions and radii by `HAND_JOINT_NAMES` index. */
export interface HandJoints {
  readonly handedness: 'left' | 'right'
  /** Is the runtime reporting this hand's joints this frame? */
  present: boolean
  /** x, y, z per joint, reference-space metres. */
  readonly at: Float32Array
  /** Metres. */
  readonly radius: Float32Array
  /** 1 where the joint was seen this frame, 0 where it was not (drawn hidden). */
  readonly seen: Uint8Array
}

export function emptyHandJoints(handedness: 'left' | 'right'): HandJoints {
  return {
    handedness,
    present: false,
    at: new Float32Array(HAND_JOINT_COUNT * 3),
    radius: new Float32Array(HAND_JOINT_COUNT),
    seen: new Uint8Array(HAND_JOINT_COUNT),
  }
}

/** Reads both hands' joints out of each frame. Allocates nothing per frame. */
export class HandJointsReader {
  readonly left = emptyHandJoints('left')
  readonly right = emptyHandJoints('right')

  advance(frame: XRFramePort, referenceSpace: unknown, sources: Iterable<XRInputSourcePort>): void {
    this.left.present = false
    this.right.present = false
    this.left.seen.fill(0)
    this.right.seen.fill(0)
    // **Called on the frame, never taken off it** (14 Sep 2026, the first device run of this
    // file): a DOM method held in a local is called with no `this`, and the browser answers
    // `TypeError: Illegal invocation` — once per frame, from inside the frame callback, so the
    // whole picture stopped: "machine has disappeared". The fakes in the tests are plain
    // closures and never noticed.
    if (frame.getJointPose === undefined) return
    for (const source of sources) {
      const hand = source.handedness === 'left' ? this.left : source.handedness === 'right' ? this.right : undefined
      const joints = source.hand
      if (hand === undefined || joints === undefined || joints === null) continue
      for (let i = 0; i < HAND_JOINT_COUNT; i += 1) {
        const pose = frame.getJointPose(joints.get(HAND_JOINT_NAMES[i]!), referenceSpace)
        if (pose === undefined || pose === null) continue
        const p = pose.transform.position
        hand.at[i * 3] = p.x
        hand.at[i * 3 + 1] = p.y
        hand.at[i * 3 + 2] = p.z
        hand.radius[i] = pose.radius === undefined || !(pose.radius > 0) ? DEFAULT_JOINT_RADIUS : pose.radius
        hand.seen[i] = 1
      }
      // A hand with no wrist is a hand nobody can draw; the tracker's own reader asks for the tip.
      hand.present = hand.seen[0] === 1
    }
  }
}
